import { useCallback, useEffect, useState, ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { RawFormFillerData, FormRole, FormAnswers } from 'shared-utils/types/formFiller';
import { getInitialFieldsAndAnswers, convertFormDataToAnswers } from '../../lib/formFiller';
import toast from 'react-hot-toast';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import {
  FormFillerContext,
  FormFillerContextValue,
  FormFillerApi,
  InitialData,
  CheckboxFeaturesMap,
  CheckboxSubmitData,
} from './FormFillerContext';

export type {
  CheckboxFeatureConfig,
  CheckboxFeatureKey,
  CheckboxFeaturesMap,
  CheckboxSubmitData,
  LoadedInitialData,
  InitialData,
  FormFillerContextValue,
  FormFillerProviderProps,
  FormInfoResponse,
  FormFillerApi,
} from './FormFillerContext';

export { FormFillerContext } from './FormFillerContext';

export function FormFillerProvider({
  token,
  role,
  api,
  children,
  formName,
  allowSignatureUpload,
  checkboxFeatures = {},
}: {
  children: ReactNode;
  formName: string;
  allowSignatureUpload: boolean;
  token: string;
  role: FormRole;
  api: FormFillerApi;
  checkboxFeatures?: CheckboxFeaturesMap;
}) {
  const [initialData, setInitialData] = useState<InitialData>({
    status: 'loading',
  });

  const [patientSignatureImageUrl, setPatientSignatureImageUrl] = useState<string | null>(null);
  const [prescriberSignatureImageUrl, setPrescriberSignatureImageUrl] = useState<string | null>(null);
  const [signatureImageS3Key, setSignatureImageS3Key] = useState<string | null>(null);
  const [shouldShowSuccessScreen, setShouldShowSuccessScreen] = useState(false);
  const [forceRefetchInitialData, setForceRefetchInitialData] = useState(0);
  const [derivedCheckboxFeatures, setDerivedCheckboxFeatures] = useState<CheckboxFeaturesMap>({});

  const syncFormAnswersMutation = useMutation({
    mutationFn: async (args: { answers: FormAnswers; signatureImageS3Key: string | null }) => {
      const data = await api.setFormAnswers({
        token,
        answers: args.answers,
        signatureImageS3Key: args.signatureImageS3Key,
      });
      return data;
    },
  });

  const setSignature = useCallback(
    (data: { previewUrl: string; s3Key: string } | null) => {
      setSignatureImageS3Key(data?.s3Key ?? null);
      switch (role) {
        case 'Patient':
          setPatientSignatureImageUrl(data?.previewUrl ?? null);
          break;
        case 'Prescriber':
          setPrescriberSignatureImageUrl(data?.previewUrl ?? null);
          break;
        default: {
          const _exhaustiveCheck: never = role;
          throw new Error(`Invalid role: ${role}`);
        }
      }
    },
    [role],
  );

  const uploadSignatureMutation = useMutation({
    mutationFn: async (file: File) => {
      const data = await api.uploadSignature(file, token);
      return {
        s3Key: data.s3Key,
        previewUrl: data.previewUrl,
      };
    },
    onError: (_error) => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const completeFormMutation = useMutation({
    mutationFn: async (args: {
      answers: FormAnswers;
      signatureImageS3Key: string | null;
      providerAttestedPatientConsent?: boolean;
    }) => {
      const data = await api.completeForm({
        token,
        answers: args.answers,
        signatureImageS3Key: args.signatureImageS3Key,
        providerAttestedPatientConsent: args.providerAttestedPatientConsent,
      });
      if (data.status !== 'ok') {
        throw new Error('Failed to complete form');
      }
    },
    onError: (_error) => {
      toast.custom(() => <StandardErrorToast />);
    },
    onSuccess: () => {
      // Redirect to a success page
      setShouldShowSuccessScreen(true);
    },
  });
  useEffect(() => {
    // This is a one time initialization function hitting a couple different endpoints, so explicitly
    // fetching the appropriate endpoints vs. using `useQuery`
    const fetchTemplate = async () => {
      const templateData = await api.getFormFillerTemplateJson(token);
      const initialAnswersResponse = await api.getFormFillerAnswers(token);
      const { formFields, initialAnswers } = getInitialFieldsAndAnswers(
        templateData.template,
        initialAnswersResponse.patient_answers.answers,
        initialAnswersResponse.prescriber_answers.answers,
        role,
      );
      setInitialData({
        status: 'success',
        formFields,
        initialAnswers,
        basePdfUrl: api.getFormFillerBasePdfUrl(token),
        formName,
        allowSignatureUpload,
        conditionalFields: templateData.conditionalFields,
        roleCompletedAt:
          role === 'Patient'
            ? initialAnswersResponse.patient_answers.completed_at
            : initialAnswersResponse.prescriber_answers.completed_at,
        hasRoleCompleted:
          role === 'Patient'
            ? initialAnswersResponse.patient_answers.completed_at !== null
            : initialAnswersResponse.prescriber_answers.completed_at !== null,
        isDocumentCompleted:
          initialAnswersResponse.patient_answers.completed_at !== null &&
          initialAnswersResponse.prescriber_answers.completed_at !== null,
      });
      setPatientSignatureImageUrl(initialAnswersResponse.patient_answers.signature_image_url);
      setPrescriberSignatureImageUrl(initialAnswersResponse.prescriber_answers.signature_image_url);

      // Fetch form info to derive checkbox features for provider attestation
      if (api.getFormInfo && role === 'Prescriber') {
        const formInfo = await api.getFormInfo(token);
        if (formInfo.supports_provider_attestation && formInfo.drug?.brand_name) {
          setDerivedCheckboxFeatures({
            providerAttestedPatientConsent: { drugName: formInfo.drug.brand_name },
          });
        }
      }
    };
    void fetchTemplate().catch((e) => {
      console.error('Error fetching template', e);
      setInitialData({
        status: 'error',
      });
    });
  }, [token, role, api, formName, allowSignatureUpload, forceRefetchInitialData]);

  const handleSubmit = useCallback(
    async (data: RawFormFillerData, checkboxData?: CheckboxSubmitData) => {
      if (initialData.status !== 'success') {
        console.error('Trying to submit before initial data is loaded');
        return;
      }
      const { data: filteredData } = convertFormDataToAnswers({
        data,
        formFields: initialData.formFields,
      });
      await completeFormMutation.mutateAsync({
        answers: filteredData,
        signatureImageS3Key: signatureImageS3Key,
        // Map checkbox data to API expected format
        providerAttestedPatientConsent: checkboxData?.providerAttestedPatientConsent,
      });
    },
    [completeFormMutation, initialData, signatureImageS3Key],
  );

  const handleSave = useCallback(
    async (data: RawFormFillerData) => {
      if (initialData.status !== 'success') {
        console.error('Trying to save before initial data is loaded');
        return;
      }
      if (completeFormMutation.isPending) {
        // Let the complete mutation handle saving the answers
        return;
      }
      const { data: filteredData } = convertFormDataToAnswers({
        data,
        formFields: initialData.formFields,
      });
      await syncFormAnswersMutation.mutateAsync({
        answers: filteredData,
        signatureImageS3Key,
      });
      return;
    },
    [initialData, syncFormAnswersMutation, signatureImageS3Key, completeFormMutation],
  );

  const setShouldShowSuccessScreenHandler = useCallback(
    (status: boolean) => {
      // Force a refetch if we're switching from the success screen back to the completed form page
      if (status === false) {
        setForceRefetchInitialData((prev) => prev + 1);
      }
      setShouldShowSuccessScreen(status);
    },
    [setShouldShowSuccessScreen],
  );

  // Merge derived checkbox features with prop (prop takes precedence for backward compatibility)
  const mergedCheckboxFeatures: CheckboxFeaturesMap = {
    ...derivedCheckboxFeatures,
    ...checkboxFeatures,
  };

  const contextValue: FormFillerContextValue = {
    role,
    token,
    initialData,
    signatureImageUrls: {
      patient: patientSignatureImageUrl,
      prescriber: prescriberSignatureImageUrl,
    },
    checkboxFeatures: mergedCheckboxFeatures,
    setSignature,
    handleSubmit,
    handleSave,
    uploadSignature: uploadSignatureMutation,
    shouldShowSuccessScreen,
    setShouldShowSuccessScreen: setShouldShowSuccessScreenHandler,
    getBasePdfDownloadUrl: api.getBasePdfDownloadUrl,
    getCompletedDocumentUrl: api.getCompletedDocumentUrl,
  };

  return <FormFillerContext.Provider value={contextValue}>{children}</FormFillerContext.Provider>;
}
